/**
 * Outline edit chip stack — renders above the Ivy composer.
 *
 * Each pill = one outline change the user made (archetype switch, reorder, description edit) that is
 * queued, not yet sent. Pills are removable and expand on click into an editable instruction. There
 * is NO send button here: on the normal composer Send, the queued edits are appended to the typed
 * message as one combined instruction and the queue is cleared (see useEmbeddedIvyRuntime.onNew).
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, X, Sparkles, ArrowUpDown, Pencil, Send, MessageSquare, Clapperboard } from 'lucide-react';
import { cn } from '../../lib/utils';
import { AutoResizeTextarea } from '../ui/auto-resize-textarea';
import { useOutlineEditQueueStore, composeQueuedMessage, queuedContext, type OutlineEditItem, type OutlineEditKind } from '../../stores/outlineEditQueueStore';
import { useEmbeddedIvyChatStore } from '../../stores/embeddedIvyChatStore';
import { studioBOn } from '../../lib/studioB/flag';
import { QuestionButton } from '../studio/plan/Thread';

const KIND_ICON: Record<OutlineEditKind, typeof Sparkles> = {
  archetype: Sparkles,
  reorder: ArrowUpDown,
  edit: Pencil,
  comment: MessageSquare,
  'storyboard-input': Clapperboard,
};
const KIND_STYLE: Record<OutlineEditKind, string> = {
  archetype: 'text-primary dark:text-accent-foreground',
  reorder: 'text-amber-600 dark:text-amber-400',
  edit: 'text-primary dark:text-accent-foreground',
  comment: 'text-primary dark:text-accent-foreground',
  'storyboard-input': 'text-emerald-600 dark:text-emerald-400',
};

interface Props {
  conversationId: string;
}

export function OutlineEditChipStack({ conversationId }: Props) {
  const items = useOutlineEditQueueStore((s) => s.queuesByConversation[conversationId] ?? []);
  const remove = useOutlineEditQueueStore((s) => s.remove);
  const updateMessage = useOutlineEditQueueStore((s) => s.updateMessage);
  const clear = useOutlineEditQueueStore((s) => s.clear);
  const [collapsed, setCollapsed] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  if (items.length === 0) return null;
  const b = studioBOn();
  if (b && items.every((it) => it.kind === 'storyboard-input')) {
    return <QueuedLine items={items} onRemove={(id) => remove(conversationId, id)} />;
  }

  // Send just the queued edits (the no-typed-text path). Typing + the main composer Send also merges
  // these in (see useEmbeddedIvyRuntime.onNew); clearing the queue here prevents any double-send.
  const sendItems = async (batch: OutlineEditItem[], after: () => void) => {
    if (sending) return;
    setSending(true);
    try {
      await useEmbeddedIvyChatStore.getState().sendMessage(composeQueuedMessage('', batch), queuedContext(batch));
      after();
    } finally {
      setSending(false);
    }
  };
  const handleSend = () => sendItems(items, () => clear(conversationId));
  const sendOne = (item: OutlineEditItem) => sendItems([item], () => remove(conversationId, item.id));

  const edits = items.filter((it) => it.kind !== 'storyboard-input').length;
  return (
    <div data-queue-tray className={b ? 'mx-3 mt-2 rounded-[var(--b-radius-card)] border border-b-line bg-b-panel' : 'border-t border-border-secondary bg-background/60'}>
      <button
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
        className={b
          ? 'flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left text-b-meta text-b-t2 hover:text-b-t1'
          : 'w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider studio-text-secondary hover:studio-text-primary'}
      >
        {b ? (
          <span>{edits} change{edits === 1 ? '' : 's'} to the plan · {edits === 1 ? 'goes' : 'go'} with your next message</span>
        ) : (
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] tabular-nums">
            {items.length}
          </span>
          {items.every((it) => it.kind === 'storyboard-input') ? 'Storyboard input' : 'Outline change'}{items.length === 1 ? '' : 's'} queued · sent with your next message
        </span>
        )}
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', collapsed && '-rotate-90')} />
      </button>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <ul className="px-2 pb-2 space-y-1 max-h-[240px] overflow-y-auto">
              {items.map((item) => (
                <OutlineEditChip
                  key={item.id}
                  b={b}
                  item={item}
                  expanded={expandedId === item.id}
                  onToggle={() => setExpandedId((id) => (id === item.id ? null : item.id))}
                  onChangeMessage={(text) => updateMessage(conversationId, item.id, text)}
                  onRemove={() => remove(conversationId, item.id)}
                  onSendNow={item.kind === 'storyboard-input' && !sending ? () => sendOne(item) : undefined}
                />
              ))}
            </ul>
            {b ? (
              <div className="flex flex-wrap items-center gap-2 px-2.5 pb-2.5">
                <QuestionButton label={sending ? 'Sending…' : 'Send now'} recommended disabled={sending} onClick={handleSend} />
                <span className="text-b-meta text-b-t3">or type and send; {edits === 1 ? 'it goes' : 'they go'} with your words</span>
              </div>
            ) : (
            <div className="px-2 pb-2">
              <button
                onClick={handleSend}
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-[12px] font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-3.5 h-3.5" />
                {sending ? 'Sending…' : `Send ${items.length} change${items.length === 1 ? '' : 's'} to Ivy`}
              </button>
              <p className="mt-1 text-[10px] text-muted-foreground/70 text-center">or type a note and send — your changes ride along</p>
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface ChipProps {
  b?: boolean;
  item: OutlineEditItem;
  expanded: boolean;
  onToggle: () => void;
  onChangeMessage: (text: string) => void;
  onRemove: () => void;
  onSendNow?: () => void;
}

function OutlineEditChip({ b = false, item, expanded, onToggle, onChangeMessage, onRemove, onSendNow }: ChipProps) {
  const Icon = KIND_ICON[item.kind];
  return (
    <li className={b ? 'rounded-[var(--b-radius-control)] bg-b-raised text-b-t1' : 'rounded-md bg-muted/60 hover:bg-muted transition-colors'}>
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <Icon className={cn('w-3.5 h-3.5 flex-shrink-0', KIND_STYLE[item.kind])} />
        <button onClick={onToggle} className="flex-1 min-w-0 text-left text-[12px] text-foreground truncate">
          {item.summary}
        </button>
        <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground/60 transition-transform flex-shrink-0', !expanded && '-rotate-90')} onClick={onToggle} />
        {onSendNow && (
          <button
            onClick={onSendNow}
            className="px-1.5 py-0.5 rounded text-[11px] font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 transition-colors flex-shrink-0"
            title="Send this input to Ivy now"
          >
            Send now
          </button>
        )}
        <button
          onClick={onRemove}
          className="p-0.5 rounded text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0"
          title="Remove this change"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden px-2 pb-2"
          >
            {item.kind === 'storyboard-input' ? (
              <code className="block w-full text-[11px] text-muted-foreground bg-surface border border-border rounded px-2 py-1.5 break-all">
                {item.message}
              </code>
            ) : (
              <AutoResizeTextarea
                value={item.message}
                onChange={(e) => onChangeMessage(e.target.value)}
                className="w-full text-[12px] text-foreground bg-surface border border-border rounded px-2 py-1.5 resize-none outline-none focus:border-foreground/20"
                minRows={2}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

/**
 * Direction B (F4): storyboard sends waiting for Ivy, as one quiet line above the box. They go by themselves when her
 * turn ends, or as soon as her conversation has loaded when she is free (useStoryboardAutoSend); Remove takes one back.
 */
function QueuedLine({ items, onRemove }: { items: OutlineEditItem[]; onRemove: (id: string) => void }) {
  const busy = useEmbeddedIvyChatStore((s) => s.isStreaming);
  const [first] = items;
  const words = items.length === 1 ? first.change?.words ?? 'Your storyboard change' : `${items.length} storyboard changes`;
  const when = busy ? `Ivy will pick ${items.length === 1 ? 'this' : 'these'} up next` : 'Sending to Ivy…';
  return (
    <div data-queued-line className="flex items-center gap-2 px-3.5 pt-2 text-b-meta text-b-t3">
      {items.length === 1 && first.change?.image && <img src={first.change.image} alt="" className="h-4 w-7 flex-none rounded-[3px] object-cover" onError={(e) => { e.currentTarget.hidden = true; }} />}
      <span className="min-w-0 flex-1 truncate">{words} · {when}</span>
      <button type="button" aria-label="Remove" title="Remove" onClick={() => items.forEach((it) => onRemove(it.id))} className="grid h-5 w-5 flex-none place-items-center rounded-full text-b-t3 transition-colors hover:bg-b-raised hover:text-b-t1">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
